import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, BellRing, Trash2, Check, Pencil, RotateCcw, XCircle, History, ExternalLink, Sparkles, User } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState, Disclaimer } from "@/components/common";
import { OBLIGATION_TYPES, DEADLINE_PRIORITIES, RECURRENCE_OPTIONS, DEADLINE_STATE_META, CONFIDENCE_META } from "@/lib/constants";import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALERT_OPTIONS = [90, 60, 30, 15, 7, 3, 1];
const EMPTY = {
    process_id: "", obligation_type: "", title: "", description: "", start_date: "", due_date: "",
    priority: "MEDIA", responsible: "", notes: "", origin_note: "",
    alert_days: [30, 15, 7, 3, 1], email_alerts: true, recurrence: "none", recurrence_interval_days: "",
};

const TABS = [
    { key: "todos", label: "Todos" },
    { key: "automaticos", label: "Automáticos" },
    { key: "manuais", label: "Manuais" },
    { key: "titulos", label: "Títulos" },
    { key: "vencendo", label: "Vencendo" },
    { key: "vencidos", label: "Vencidos" },
    { key: "concluidos", label: "Concluídos" },
    { key: "pendentes", label: "Pendentes de confirmação" },
];

function fmtDate(s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString("pt-BR"); } catch { return s; }
}

function daysText(d) {
    if (d.state === "CONCLUIDO") return d.completed_at ? `Concluído em ${fmtDate(d.completed_at)}` : "Concluído";
    if (d.state === "CANCELADO") return "Cancelado";
    const n = d.days_left;
    if (n === null || n === undefined) return "A confirmar";
    if (n < 0) return `Vencido há ${Math.abs(n)} dia${Math.abs(n) === 1 ? "" : "s"}`;
    if (n === 0) return "Vence hoje";
    if (n === 1) return "Vence amanhã";
    return `${n} dias restantes`;
}

function StateBadge({ state }) {
    const m = DEADLINE_STATE_META[state] || DEADLINE_STATE_META.ABERTO;
    return <span data-testid={`deadline-state-${state}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${m.cls}`}>{m.label}</span>;
}

function PriorityBadge({ priority }) {
    const m = DEADLINE_PRIORITIES.find((p) => p.value === priority) || DEADLINE_PRIORITIES[1];
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border ${m.cls}`}>{m.label}</span>;
}

function OriginBadge({ d }) {
    if (d.creation_type === "AUTO") {
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold border bg-mineral/10 text-mineralHover border-mineral/20"><Sparkles className="w-3 h-3" /> AUTOMÁTICO GM²</span>;
    }
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold border bg-slate-100 text-slate-600 border-slate-200"><User className="w-3 h-3" /> CADASTRADO PELO USUÁRIO</span>;
}

function ConfidenceBadge({ c }) {
    const m = CONFIDENCE_META[c];
    if (!m) return null;
    const cls = c === "CONFIRMED" ? "bg-green-50 text-green-700 border-green-200"
        : c === "CALCULATED" ? "bg-blue-50 text-blue-700 border-blue-200"
        : "bg-purple-50 text-purple-700 border-purple-200";
    return <span title={m.desc} className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border ${cls}`}>{m.label}</span>;
}

export default function Deadlines() {
    const [items, setItems] = useState([]);
    const [processes, setProcesses] = useState([]);
    const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "todos");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [editId, setEditId] = useState(null);
    const [editDoc, setEditDoc] = useState(null);
    const [saving, setSaving] = useState(false);
    const [completeFor, setCompleteFor] = useState(null);
    const [completion, setCompletion] = useState({ completed_at: "", protocol: "", document: "", note: "" });
    const [historyFor, setHistoryFor] = useState(null);
    const [history, setHistory] = useState([]);
    const [titleFor, setTitleFor] = useState(null);
    const [titleForm, setTitleForm] = useState({});

    const load = () => api.get("/deadlines").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => {
        load();
        api.get("/processes").then((r) => setProcesses(r.data)).catch(() => {});
    }, []);

    const counts = useMemo(() => {
        const active = items.filter((d) => !["CONCLUIDO", "CANCELADO"].includes(d.state));
        return {
            hoje: active.filter((d) => d.days_left === 0).length,
            d7: active.filter((d) => d.days_left !== null && d.days_left >= 0 && d.days_left <= 7).length,
            d30: active.filter((d) => d.days_left !== null && d.days_left >= 0 && d.days_left <= 30).length,
            d60: active.filter((d) => d.days_left !== null && d.days_left >= 0 && d.days_left <= 60).length,
            vencidos: active.filter((d) => d.state === "VENCIDO").length,
            pendentes: items.filter((d) => d.state === "PENDENTE_CONFIRMACAO").length,
            concluidos: items.filter((d) => d.state === "CONCLUIDO").length,
        };
    }, [items]);

    const filtered = useMemo(() => {
        switch (tab) {
            case "automaticos": return items.filter((d) => d.creation_type === "AUTO");
            case "manuais": return items.filter((d) => d.creation_type !== "AUTO");
            case "vencendo": return items.filter((d) => ["URGENTE", "PROXIMO"].includes(d.state));
            case "vencidos": return items.filter((d) => d.state === "VENCIDO");
            case "concluidos": return items.filter((d) => d.state === "CONCLUIDO");
            case "pendentes": return items.filter((d) => d.state === "PENDENTE_CONFIRMACAO");
            default: return items;
        }
    }, [items, tab]);

    const toggleAlert = (d) => {
        const has = form.alert_days.includes(d);
        setForm({ ...form, alert_days: has ? form.alert_days.filter((x) => x !== d) : [...form.alert_days, d] });
    };

    const openNew = () => { setForm(EMPTY); setEditId(null); setEditDoc(null); setOpen(true); };
    const openEdit = (d) => {
        setForm({
            process_id: d.process_id || "", obligation_type: d.obligation_type, title: d.title || "",
            description: d.description || "", start_date: (d.start_date || "").slice(0, 10),
            due_date: (d.due_date || "").slice(0, 10), priority: d.priority || "MEDIA",
            responsible: d.responsible || "", notes: d.notes || "", origin_note: d.origin_note || "",
            alert_days: d.alert_days || [], email_alerts: d.email_alerts ?? true,
            recurrence: d.recurrence || "none", recurrence_interval_days: d.recurrence_interval_days || "",
        });
        setEditId(d.id); setEditDoc(d); setOpen(true);
    };

    const submit = async () => {
        if (!form.obligation_type || !form.due_date) { toast.error("Informe o tipo e a data de vencimento."); return; }
        setSaving(true);
        try {
            const payload = {
                ...form, process_id: form.process_id || null,
                recurrence_interval_days: form.recurrence === "personalizado" ? Number(form.recurrence_interval_days) || null : null,
            };
            if (editId) await api.put(`/deadlines/${editId}`, payload);
            else await api.post("/deadlines", payload);
            toast.success(editId ? "Prazo atualizado." : "Prazo criado.");
            setOpen(false); load();
        } catch (e) { toast.error("Erro ao salvar prazo."); }
        finally { setSaving(false); }
    };

    const doComplete = async () => {
        try {
            await api.post(`/deadlines/${completeFor.id}/complete`, {
                completed_at: completion.completed_at || null, protocol: completion.protocol,
                document: completion.document, note: completion.note,
            });
            toast.success("Prazo concluído.");
            setCompleteFor(null); setCompletion({ completed_at: "", protocol: "", document: "", note: "" }); load();
        } catch { toast.error("Erro ao concluir."); }
    };
    const reopen = async (id) => { await api.post(`/deadlines/${id}/reopen`); load(); };
    const cancel = async (id) => { await api.post(`/deadlines/${id}/cancel`); toast.success("Prazo cancelado."); load(); };
    const confirmAuto = async (d) => {
        if (!d.due_date) { openEdit(d); return; }
        try { await api.post(`/deadlines/${d.id}/confirm`); toast.success("Prazo confirmado."); load(); }
        catch { toast.error("Não foi possível confirmar. Edite e informe o vencimento."); }
    };
    const del = async (id) => { await api.delete(`/deadlines/${id}`); toast.success("Prazo excluído."); load(); };
    const openHistory = async (d) => {
        setHistoryFor(d);
        try { const r = await api.get(`/deadlines/${d.id}/history`); setHistory(r.data); } catch { setHistory([]); }
    };

    const openTitle = (p) => {
        setTitleForm({
            title_number: p.title_number || "", title_type: p.title_type || "", title_status: p.title_status || "",
            title_publication_date: (p.title_publication_date || "").slice(0, 10), title_grant_date: (p.title_grant_date || "").slice(0, 10),
            title_start_date: (p.title_start_date || "").slice(0, 10), title_end_date: (p.title_end_date || "").slice(0, 10),
            title_source: p.title_source || "MANUAL",
        });
        setTitleFor(p);
    };
    const saveTitle = async () => {
        try {
            await api.put(`/processes/${titleFor.id}/title`, titleForm);
            toast.success("Vigência do título salva.");
            setTitleFor(null);
            api.get("/processes").then((r) => setProcesses(r.data)).catch(() => {});
            load();
        } catch { toast.error("Erro ao salvar título."); }
    };

    return (
        <div className="space-y-6" data-testid="deadlines-page">
            <PageHeader title="Prazos e Obrigações" subtitle="Central de prazos ANM — automáticos (GM²) e cadastrados por você, com cálculo de dias restantes."
                action={<button onClick={openNew} data-testid="add-deadline-button" className="bg-mineral hover:bg-mineralHover text-white rounded-sm px-5 py-2.5 font-bold shadow-md flex items-center gap-2 transition-colors"><Plus className="w-4 h-4" /> Cadastrar prazo manual</button>} />

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="deadline-kpis">
                {[
                    { k: "Vencendo hoje", v: counts.hoje, c: "text-orange-600" },
                    { k: "Próx. 7 dias", v: counts.d7, c: "text-[#e66e00]" },
                    { k: "Próx. 30 dias", v: counts.d30, c: "text-yellow-600" },
                    { k: "Próx. 60 dias", v: counts.d60, c: "text-slate-700" },
                    { k: "Vencidos", v: counts.vencidos, c: "text-red-600" },
                    { k: "Pendentes", v: counts.pendentes, c: "text-purple-600" },
                    { k: "Concluídos", v: counts.concluidos, c: "text-emerald-600" },
                ].map((x) => (
                    <div key={x.k} className="bg-white border border-slate-200 rounded-md p-3 shadow-sm">
                        <div className={`text-2xl font-bold font-heading ${x.c}`}>{x.v}</div>
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">{x.k}</div>
                    </div>
                ))}
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
                {TABS.map((t) => (
                    <button key={t.key} data-testid={`deadline-tab-${t.key}`} onClick={() => setTab(t.key)}
                        className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "bg-petroleum text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{t.label}</button>
                ))}
            </div>

            {tab === "titulos" ? (
                <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto" data-testid="titles-table">
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 border-b-2 border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                            <th className="py-3 px-4">Processo</th><th className="py-3 px-4">Título</th><th className="py-3 px-4">Início vigência</th>
                            <th className="py-3 px-4">Término vigência</th><th className="py-3 px-4">Fonte</th><th className="py-3 px-4">Última atualização</th><th className="py-3 px-4"></th>
                        </tr></thead>
                        <tbody>
                            {processes.map((p) => (
                                <tr key={p.id} data-testid={`title-row-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                                    <td className="py-3 px-4 font-mono text-petroleum font-semibold">{p.process_number}</td>
                                    <td className="py-3 px-4">{p.title_number ? `${p.title_type || ""} ${p.title_number}`.trim() : <span className="text-slate-400">Não informado</span>}</td>
                                    <td className="py-3 px-4 font-mono">{p.title_start_date ? fmtDate(p.title_start_date) : <span className="text-slate-400">Não informada</span>}</td>
                                    <td className="py-3 px-4 font-mono">{p.title_end_date ? fmtDate(p.title_end_date) : <span className="text-slate-400">Não informada</span>}</td>
                                    <td className="py-3 px-4 text-xs text-slate-500">{p.title_source || "—"}</td>
                                    <td className="py-3 px-4 text-xs text-slate-500">{p.title_last_verified_at ? fmtDate(p.title_last_verified_at) : "—"}</td>
                                    <td className="py-3 px-4 text-right"><button onClick={() => openTitle(p)} data-testid={`edit-title-${p.id}`} className="px-3 py-1 rounded text-xs font-semibold bg-petroleum text-white hover:bg-petroleumHover">Editar título</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {processes.length === 0 && <div className="p-8 text-center text-sm text-slate-400">Cadastre processos em "Meus Processos" para gerenciar a vigência dos títulos.</div>}
                </div>
            ) : filtered.length === 0 ? (
                <EmptyState icon={BellRing} title="Nenhum prazo nesta aba" desc="Cadastre obrigações e vencimentos ou aguarde a detecção automática do GM²." />
            ) : (                <div className="grid gap-3">
                    {filtered.map((d) => (
                        <div key={d.id} data-testid={`deadline-item-${d.id}`} className={`bg-white border rounded-md shadow-sm p-4 ${d.state === "VENCIDO" ? "border-red-200" : "border-slate-200"} ${["CONCLUIDO", "CANCELADO"].includes(d.state) ? "opacity-70" : ""}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center flex-wrap gap-2">
                                        <span className="font-heading font-bold text-slate-800">{d.obligation_type}</span>
                                        <StateBadge state={d.state} />
                                        <PriorityBadge priority={d.priority} />
                                        <OriginBadge d={d} />
                                        {d.creation_type === "AUTO" && <ConfidenceBadge c={d.deadline_confidence} />}
                                    </div>
                                    {d.title && d.title !== d.obligation_type && <div className="text-sm text-slate-700 mt-1">{d.title}</div>}
                                    <div className="text-xs text-slate-500 mt-1">
                                        {d.process_number ? <span className="font-mono text-petroleum font-semibold">Processo {d.process_number}</span> : "Sem processo vinculado"}
                                        {" · "}Vencimento: <span className="font-mono">{fmtDate(d.due_date)}</span>
                                    </div>
                                    <div className="text-sm font-semibold mt-1 text-slate-700">{daysText(d)}</div>
                                    {d.description && <div className="text-xs text-slate-500 mt-1">{d.description}</div>}
                                    {d.creation_type === "AUTO" && d.source_label && (
                                        <div className="text-[11px] text-mineralHover mt-1">{`Gerado a partir de: ${d.source_label}`}</div>
                                    )}
                                    {d.edited && d.original_deadline_date && (
                                        <div className="text-[11px] text-slate-400 mt-0.5">Vencimento originalmente detectado pelo GM²: {fmtDate(d.original_deadline_date)}</div>
                                    )}
                                    {d.source_url && (
                                        <a href={d.source_url} target="_blank" rel="noreferrer" data-testid={`deadline-source-${d.id}`} className="inline-flex items-center gap-1 text-xs text-petroleum underline mt-1"><ExternalLink className="w-3 h-3" /> Ver fonte oficial</a>
                                    )}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {d.creation_type === "AUTO" && !d.confirmed && d.state !== "CONCLUIDO" && d.state !== "CANCELADO" && (
                                        <button onClick={() => confirmAuto(d)} title="Confirmar prazo" data-testid={`confirm-deadline-${d.id}`} className="px-2 py-1 rounded text-xs font-semibold bg-mineral text-white hover:bg-mineralHover transition-colors">Confirmar</button>
                                    )}
                                    {d.state !== "CONCLUIDO" ? (
                                        <button onClick={() => { setCompleteFor(d); setCompletion({ completed_at: "", protocol: "", document: "", note: "" }); }} title="Marcar como concluído" data-testid={`complete-deadline-${d.id}`} className="p-1.5 text-slate-400 hover:text-green-600 transition-colors"><Check className="w-4 h-4" /></button>
                                    ) : (
                                        <button onClick={() => reopen(d.id)} title="Reabrir" data-testid={`reopen-deadline-${d.id}`} className="p-1.5 text-slate-400 hover:text-petroleum transition-colors"><RotateCcw className="w-4 h-4" /></button>
                                    )}
                                    <button onClick={() => openEdit(d)} title="Editar / revisar" data-testid={`edit-deadline-${d.id}`} className="p-1.5 text-slate-400 hover:text-petroleum transition-colors"><Pencil className="w-4 h-4" /></button>
                                    <button onClick={() => openHistory(d)} title="Histórico" data-testid={`history-deadline-${d.id}`} className="p-1.5 text-slate-400 hover:text-petroleum transition-colors"><History className="w-4 h-4" /></button>
                                    {d.state !== "CANCELADO" && d.state !== "CONCLUIDO" && (
                                        <button onClick={() => cancel(d.id)} title="Cancelar" data-testid={`cancel-deadline-${d.id}`} className="p-1.5 text-slate-400 hover:text-amber-600 transition-colors"><XCircle className="w-4 h-4" /></button>
                                    )}
                                    <button onClick={() => del(d.id)} title="Excluir" data-testid={`delete-deadline-${d.id}`} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Disclaimer>O GM² nunca inventa prazos. Datas automáticas só são geradas quando há data explícita no ato oficial ou regra confirmada cadastrada. Confirme sempre na fonte oficial (ANM / DOU).</Disclaimer>

            {/* Form dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle className="font-heading">{editId ? (editDoc?.creation_type === "AUTO" ? "Revisar prazo automático" : "Editar prazo") : "Cadastrar prazo manual"}</DialogTitle></DialogHeader>
                    <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                        {editDoc?.creation_type === "AUTO" && (
                            <div className="rounded-md border border-mineral/20 bg-mineral/5 p-3 text-xs text-slate-600" data-testid="deadline-auto-banner">
                                <div className="font-semibold text-mineralHover mb-1">Prazo detectado automaticamente pelo GM²</div>
                                {editDoc.source_label && <div>Fonte: {editDoc.source_label}</div>}
                                {editDoc.deadline_confidence && CONFIDENCE_META[editDoc.deadline_confidence] && (
                                    <div className="mt-1">{CONFIDENCE_META[editDoc.deadline_confidence].desc}</div>
                                )}
                                <div className="mt-1 text-slate-400">A edição não apaga os valores originalmente detectados pelo GM².</div>
                            </div>
                        )}
                        <div>
                            <Label className="mb-1 block">Processo (opcional)</Label>
                            <Select value={form.process_id || "none"} onValueChange={(v) => setForm({ ...form, process_id: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="deadline-process"><SelectValue placeholder="Sem processo" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Sem processo vinculado</SelectItem>
                                    {processes.map((p) => <SelectItem key={p.id} value={p.id}>{p.process_number} — {p.substance}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="mb-1 block">Tipo de obrigação *</Label>
                                <Select value={form.obligation_type} onValueChange={(v) => setForm({ ...form, obligation_type: v })}>
                                    <SelectTrigger data-testid="deadline-type"><SelectValue placeholder="Selecione" /></SelectTrigger>
                                    <SelectContent>{OBLIGATION_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="mb-1 block">Prioridade</Label>
                                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                                    <SelectTrigger data-testid="deadline-priority"><SelectValue /></SelectTrigger>
                                    <SelectContent>{DEADLINE_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div><Label className="mb-1 block">Título</Label><Input data-testid="deadline-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex.: Cumprir exigência do Despacho X" /></div>
                        <div><Label className="mb-1 block">Descrição</Label><Input data-testid="deadline-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="mb-1 block">Data de início</Label><Input type="date" data-testid="deadline-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                            <div><Label className="mb-1 block">Data de vencimento *</Label><Input type="date" data-testid="deadline-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="mb-1 block">Responsável</Label><Input data-testid="deadline-responsible" value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} /></div>
                            <div><Label className="mb-1 block">Origem</Label><Input data-testid="deadline-origin" value={form.origin_note} onChange={(e) => setForm({ ...form, origin_note: e.target.value })} placeholder="Ex.: Contrato, decisão interna" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="mb-1 block">Recorrência</Label>
                                <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                                    <SelectTrigger data-testid="deadline-recurrence"><SelectValue /></SelectTrigger>
                                    <SelectContent>{RECURRENCE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            {form.recurrence === "personalizado" && (
                                <div><Label className="mb-1 block">Intervalo (dias)</Label><Input type="number" data-testid="deadline-recurrence-days" value={form.recurrence_interval_days} onChange={(e) => setForm({ ...form, recurrence_interval_days: e.target.value })} /></div>
                            )}
                        </div>
                        <div>
                            <Label className="mb-2 block">Notificar antes (dias)</Label>
                            <div className="flex flex-wrap gap-2">
                                {ALERT_OPTIONS.map((d) => (
                                    <button key={d} type="button" onClick={() => toggleAlert(d)} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${form.alert_days.includes(d) ? "bg-petroleum text-white border-petroleum" : "bg-white text-slate-600 border-slate-200 hover:border-petroleum"}`}>{d}d</button>
                                ))}
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input type="checkbox" checked={form.email_alerts} onChange={(e) => setForm({ ...form, email_alerts: e.target.checked })} className="rounded border-slate-300" />
                            Também notificar por e-mail
                        </label>
                        <div><Label className="mb-1 block">Observação</Label><Textarea data-testid="deadline-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
                    </div>
                    <DialogFooter>
                        {editDoc?.source_url && <a href={editDoc.source_url} target="_blank" rel="noreferrer" className="px-4 py-2 text-petroleum text-sm font-medium inline-flex items-center gap-1"><ExternalLink className="w-4 h-4" /> Ver fonte oficial</a>}
                        <button onClick={() => setOpen(false)} className="px-4 py-2 text-slate-600 text-sm font-medium">Cancelar</button>
                        <button onClick={submit} disabled={saving} data-testid="save-deadline-button" className="bg-petroleum hover:bg-petroleumHover text-white rounded-sm px-6 py-2 font-semibold transition-colors disabled:opacity-60">{saving ? "Salvando..." : (editDoc?.creation_type === "AUTO" ? "Confirmar prazo" : "Salvar")}</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Complete dialog */}
            <Dialog open={!!completeFor} onOpenChange={(v) => !v && setCompleteFor(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle className="font-heading">Concluir prazo</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label className="mb-1 block">Data de cumprimento</Label><Input type="date" data-testid="completion-date" value={completion.completed_at} onChange={(e) => setCompletion({ ...completion, completed_at: e.target.value })} /></div>
                        <div><Label className="mb-1 block">Número do protocolo</Label><Input data-testid="completion-protocol" value={completion.protocol} onChange={(e) => setCompletion({ ...completion, protocol: e.target.value })} /></div>
                        <div><Label className="mb-1 block">Documento comprobatório (referência)</Label><Input data-testid="completion-document" value={completion.document} onChange={(e) => setCompletion({ ...completion, document: e.target.value })} /></div>
                        <div><Label className="mb-1 block">Observação</Label><Textarea data-testid="completion-note" value={completion.note} onChange={(e) => setCompletion({ ...completion, note: e.target.value })} rows={2} /></div>
                        <p className="text-xs text-slate-400">O prazo concluído não é apagado — vai para o histórico e continua consultável.</p>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setCompleteFor(null)} className="px-4 py-2 text-slate-600 text-sm font-medium">Cancelar</button>
                        <button onClick={doComplete} data-testid="confirm-complete-button" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm px-6 py-2 font-semibold transition-colors">Marcar como concluído</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* History dialog */}
            <Dialog open={!!historyFor} onOpenChange={(v) => !v && setHistoryFor(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle className="font-heading">Histórico do prazo</DialogTitle></DialogHeader>
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="deadline-history-list">
                        {history.length === 0 ? <p className="text-sm text-slate-400">Sem histórico.</p> : history.map((h) => (
                            <div key={h.id} className="border-b border-slate-100 pb-2">
                                <div className="text-xs font-semibold text-slate-700 uppercase">{h.action}</div>
                                <div className="text-sm text-slate-600">{h.detail}</div>
                                <div className="text-[11px] text-slate-400">{fmtDate(h.at)} · {h.actor}</div>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Title (vigência) dialog */}
            <Dialog open={!!titleFor} onOpenChange={(v) => !v && setTitleFor(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle className="font-heading">Vigência do título — {titleFor?.process_number}</DialogTitle></DialogHeader>
                    <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">O SIGMINE não traz as datas de vigência do título e o SCM exige CAPTCHA. Informe as datas a partir do ato oficial (DOU), documento (IA) ou manualmente. O GM² nunca deriva a vigência do ano do processo.</div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="mb-1 block">Número do título</Label><Input data-testid="title-number" value={titleForm.title_number} onChange={(e) => setTitleForm({ ...titleForm, title_number: e.target.value })} /></div>
                            <div><Label className="mb-1 block">Tipo</Label><Input data-testid="title-type" value={titleForm.title_type} onChange={(e) => setTitleForm({ ...titleForm, title_type: e.target.value })} placeholder="Alvará / Portaria / Licença" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="mb-1 block">Publicação</Label><Input type="date" data-testid="title-publication" value={titleForm.title_publication_date} onChange={(e) => setTitleForm({ ...titleForm, title_publication_date: e.target.value })} /></div>
                            <div><Label className="mb-1 block">Outorga/Concessão</Label><Input type="date" data-testid="title-grant" value={titleForm.title_grant_date} onChange={(e) => setTitleForm({ ...titleForm, title_grant_date: e.target.value })} /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="mb-1 block">Início da vigência</Label><Input type="date" data-testid="title-start" value={titleForm.title_start_date} onChange={(e) => setTitleForm({ ...titleForm, title_start_date: e.target.value })} /></div>
                            <div><Label className="mb-1 block">Término da vigência</Label><Input type="date" data-testid="title-end" value={titleForm.title_end_date} onChange={(e) => setTitleForm({ ...titleForm, title_end_date: e.target.value })} /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="mb-1 block">Situação</Label><Input data-testid="title-status" value={titleForm.title_status} onChange={(e) => setTitleForm({ ...titleForm, title_status: e.target.value })} placeholder="Vigente / Vencido" /></div>
                            <div>
                                <Label className="mb-1 block">Fonte</Label>
                                <Select value={titleForm.title_source} onValueChange={(v) => setTitleForm({ ...titleForm, title_source: v })}>
                                    <SelectTrigger data-testid="title-source"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="MANUAL">Manual</SelectItem>
                                        <SelectItem value="DOU">DOU</SelectItem>
                                        <SelectItem value="IA">Documento (IA)</SelectItem>
                                        <SelectItem value="CADASTRO_MINEIRO">Cadastro Mineiro</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <p className="text-xs text-slate-400">Ao informar o término da vigência, o GM² cria/atualiza automaticamente um prazo "Vencimento de Alvará" com essa data (confirmada).</p>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setTitleFor(null)} className="px-4 py-2 text-slate-600 text-sm font-medium">Cancelar</button>
                        <button onClick={saveTitle} data-testid="save-title-button" className="bg-petroleum hover:bg-petroleumHover text-white rounded-sm px-6 py-2 font-semibold">Salvar vigência</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
